// Componente reutilizabile: sparkline, bara de metrici, rândul de monedă, selectoare.

import { el, clear, fmtVolume, fmtCount, fmtPct, fmtPrice, fmtDuration, tfLabel, splitSymbol } from '../core/util.js';
import { iconMoneda, iconBursa } from './coin-icon.js';
import { tiger } from '../core/tiger.js';
import { icon } from './icons.js';
import { settings } from '../core/state.js';
import { TIMEFRAMES } from '../core/exchanges.js';
import { chartTheme } from '../core/theme.js';

const SVG_NS = 'http://www.w3.org/2000/svg';
const UP_COLOR = '#16C784';
const DOWN_COLOR = '#F6455F';
let gradientSeq = 0;

/**
 * Sparkline cu lumânări — forma cardurilor de radar de la ScalpX.
 *
 * Nu e un grafic în miniatură, ci o formă: fără axe, fără crosshair, fără zoom. De
 * aceea nici nu instanţiem un motor de randare per card — la şaizeci de carduri care
 * se redesenează la fiecare tur de agregate ar fi şaizeci de motoare vii degeaba.
 *
 * Toate lumânările intră în patru trasee (fitile sus/jos, corpuri sus/jos) în loc de
 * patru elemente per bară: 4 noduri în DOM pe card, nu 160.
 *
 * @param {Array<[number,number,number,number]>} bars  [open, high, low, close]
 */
export function candleSparkline(bars, { width = 150, height = 44 } = {}) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('class', 'candle-spark');
  if (!bars || bars.length < 2) return svg;

  let min = Infinity;
  let max = -Infinity;
  for (const b of bars) { if (b[2] < min) min = b[2]; if (b[1] > max) max = b[1]; }
  const span = max - min || Math.abs(max) * 0.001 || 1;

  const pad = 2;
  const innerH = height - pad * 2;
  const y = (v) => pad + innerH - ((v - min) / span) * innerH;
  const pas = width / bars.length;
  const latCorp = Math.max(1, pas * 0.66);

  const fitil = { sus: '', jos: '' };
  const corp = { sus: '', jos: '' };

  bars.forEach((b, i) => {
    const [o, h, l, c] = b;
    const urca = c >= o;
    const cheie = urca ? 'sus' : 'jos';
    const x = i * pas + pas / 2;
    fitil[cheie] += `M${x.toFixed(2)} ${y(h).toFixed(2)}V${y(l).toFixed(2)}`;
    const sus = y(Math.max(o, c));
    const jos = y(Math.min(o, c));
    // Doji: fără înălţime minimă corpul dispare şi lumânarea pare un fitil rupt.
    const inalt = Math.max(1, jos - sus);
    corp[cheie] += `M${(x - latCorp / 2).toFixed(2)} ${sus.toFixed(2)}`
      + `h${latCorp.toFixed(2)}v${inalt.toFixed(2)}h${(-latCorp).toFixed(2)}z`;
  });

  const t = chartTheme();
  const traseu = (d, culoare, umple) => {
    if (!d) return;
    const el = document.createElementNS(SVG_NS, 'path');
    el.setAttribute('d', d);
    if (umple) { el.setAttribute('fill', culoare); } else {
      el.setAttribute('fill', 'none');
      el.setAttribute('stroke', culoare);
      el.setAttribute('stroke-width', '1');
    }
    svg.append(el);
  };
  traseu(fitil.sus, t.upWick, false);
  traseu(fitil.jos, t.downWick, false);
  traseu(corp.sus, t.up, true);
  traseu(corp.jos, t.down, true);
  return svg;
}

/**
 * Sparkline ca la ScalpX: linie + arie cu gradient care se stinge spre bază.
 * Implicit 78×22 — dimensiunea exactă din lista lor de monede.
 */
export function sparkline(values, { width = 78, height = 22, color = null } = {}) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('width', width);
  svg.setAttribute('height', height);
  svg.setAttribute('class', 'sparkline');
  svg.setAttribute('fill', 'none');
  if (!values || values.length < 2) return svg;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  // Marginile de 2px lasă linia să respire în loc să atingă marginile.
  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const stepX = innerW / (values.length - 1);
  const x = (i) => pad + i * stepX;
  const y = (v) => pad + innerH - ((v - min) / span) * innerH;

  const stroke = color || (values[values.length - 1] >= values[0] ? UP_COLOR : DOWN_COLOR);
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');

  const id = `spk${gradientSeq++}`;
  const defs = document.createElementNS(SVG_NS, 'defs');
  defs.innerHTML = `<linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">`
    + `<stop offset="0" stop-color="${stroke}" stop-opacity=".28"/>`
    + `<stop offset="1" stop-color="${stroke}" stop-opacity="0"/></linearGradient>`;
  svg.append(defs);

  const area = document.createElementNS(SVG_NS, 'path');
  area.setAttribute('d', `${line} L${x(values.length - 1).toFixed(1)} ${height} L${x(0).toFixed(1)} ${height} Z`);
  area.setAttribute('fill', `url(#${id})`);
  svg.append(area);

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', line);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', stroke);
  path.setAttribute('stroke-width', '1.4');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);
  return svg;
}

/**
 * Bara „технические данные" de deasupra graficului.
 *
 * Metricile și ordinea sunt cele din ScalpX: оборот (cu săgeata de trend), изменение,
 * сделки, всплеск, волатильность, фандинг, обратный отсчёт, открытый интерес,
 * корреляция, NATR. Implicit se afișează doar cifrele; butonul din dreapta
 * desface etichetele, exact ca `techdata-toggle` la ei.
 */
export function techBar(row, tech = null) {
  const compact = settings.get('sx_techdata_compact', true) !== false;
  const wrap = el('div', { class: `techdata${compact ? ' compact' : ''}` });
  if (!row) return wrap;

  const cell = (value, label, cls = '', extra = null) =>
    el('span', { class: `td-cell ${cls}`, title: label }, [
      el('span', { class: 'td-value' }, [value, extra]),
      el('span', { class: 'td-label', text: label }),
    ]);

  const change = Number(row.change24h) || 0;
  const spike = Number(row.volTrend) || 0;
  const funding = Number(row.funding) || 0;

  // Săgeata de lângă oborot arată dacă volumul crește sau scade față de normal.
  const trendArrow = Number.isFinite(row.volTrend)
    ? icon(spike >= 0 ? 'arrowUp' : 'arrowDown', 9)
    : null;
  if (trendArrow) trendArrow.classList.add(spike >= 0 ? 'up' : 'down');

  wrap.append(
    cell(fmtVolume(row.quoteVolume), 'Объём 24ч', 'td-strong', trendArrow),
    cell(fmtPct(change), 'Изменение 24ч', change >= 0 ? 'up' : 'down'),
    cell(fmtCount(row.trades), 'Сделки 24ч'),
    cell(fmtPct(spike, 0), 'Всплеск объёма', spike >= 0 ? 'up' : 'down'),
    cell(`${(row.volatility24h ?? 0).toFixed(2)}%`, 'Волатильность'),
    cell(`${funding.toFixed(4)}%`, 'Фандинг', funding >= 0 ? 'up' : 'down'),
  );

  if (row.nextFunding) {
    const countdown = el('span', { class: 'td-value' });
    const tick = () => { countdown.textContent = fmtDuration((row.nextFunding - Date.now()) / 1000); };
    tick();
    const timer = setInterval(tick, 1000);
    // Oprim ceasul când celula iese din DOM.
    new MutationObserver((_, obs) => {
      if (!countdown.isConnected) { clearInterval(timer); obs.disconnect(); }
    }).observe(document.body, { childList: true, subtree: true });

    wrap.append(el('span', { class: 'td-cell', title: 'До фандинга' }, [
      countdown, el('span', { class: 'td-label', text: 'До фандинга' }),
    ]));
  }

  if (row.openInterest) wrap.append(cell(fmtVolume(row.openInterest), 'Открытый интерес'));
  if (row.corr24 !== undefined) wrap.append(cell((row.corr24 ?? 0).toFixed(2), 'Корреляция с BTC'));
  if (tech?.natr) wrap.append(cell(`${tech.natr.toFixed(2)}%`, 'NATR'));

  wrap.append(el('button', {
    class: 'techdata-toggle',
    title: compact ? 'Показать подписи' : 'Свернуть подписи — только цифры',
    onclick: () => settings.set('sx_techdata_compact', !compact),
  }, [icon(compact ? 'chevronDown' : 'chevronUp', 9)]));

  return wrap;
}

/** Grup de butoane pentru timeframe. */
export function tfButtons(active, onSelect, { list = TIMEFRAMES, compact = false } = {}) {
  const group = el('div', { class: `tf-group${compact ? ' compact' : ''}` });
  for (const tf of list) {
    group.append(el('button', {
      class: `tf-btn${tf === active ? ' active' : ''}`,
      text: tfLabel(tf),
      title: tfTitle(tf),
      onclick: () => {
        [...group.children].forEach((c) => c.classList.remove('active'));
        group.querySelector(`[data-tf="${tf}"]`)?.classList.add('active');
        onSelect(tf);
      },
      dataset: { tf },
    }));
  }
  return group;
}

function tfTitle(tf) {
  return {
    '1m': '1 минута', '5m': '5 минут', '15m': '15 минут',
    '1h': '1 час', '4h': '4 часа', '1d': '1 день',
  }[tf] || tf;
}

/**
 * Rând de monedă din watchlist. Structura urmează ScalpX: grilă de trei coloane
 * (semn de carte · nume+volum · sparkline), cu volumul ca pilulă de 72px a cărei
 * umplere e proporțională cu oborotul relativ.
 */
/**
 * Iconița „deschide în Tiger.Trade", lângă numele monedei.
 *
 * Apare doar dacă integrarea e pornită din setări: cine n-are terminalul instalat
 * n-are ce face cu un buton care nu duce nicăieri.
 */
export function tigerBtn(symbol, exchange, market) {
  if (!tiger.enabled) return null;
  return el('button', {
    class: 'tiger-btn',
    title: `Открыть ${symbol} в Tiger.Trade`,
    onclick: (event) => {
      event.stopPropagation();
      const ok = tiger.open(symbol, exchange, market);
      if (!ok) toast('Интеграция с Tiger.Trade выключена в настройках', 'info');
    },
  }, [el('img', { class: 'tiger-mark', src: '/static/img/tiger.png', alt: 'Tiger.Trade' })]);
}

/**
 * Un rând din lista de urmărire.
 *
 * Fără butonul Tiger: coloana are 240 de pixeli şi ţine deja semn de carte, ticker,
 * schimbare, oborot şi sparkline. A şasea pictogramă în acelaşi rând nu mai adăuga
 * informaţie, doar zgomot. Butonul rămâne unde e loc pentru el — pe antetul graficului
 * şi pe cardurile de formaţii.
 */
export function coinRow(row, { spark = null, active = false, maxVolume = 1, onClick,
  bookmarked = false, onBookmark, exchange = null } = {}) {
  const change = Number(row.change24h) || 0;
  const fill = Math.max(3, Math.min(100, ((row.quoteVolume || 0) / (maxVolume || 1)) * 100));

  const volume = el('span', {
    class: 'coin-volume',
    text: fmtVolume(row.quoteVolume),
    title: `Оборот 24ч: ${fmtVolume(row.quoteVolume)}`,
  });
  volume.style.setProperty('--vol-fill', `${fill}%`);

  return el('div', {
    class: `coin-row${active ? ' active' : ''}`,
    onclick: () => onClick?.(row.symbol),
  }, [
    el('button', {
      class: `coin-bookmark${bookmarked ? ' on' : ''}`,
      title: bookmarked ? 'Убрать из закладок' : 'В закладки',
      onclick: (event) => { event.stopPropagation(); onBookmark?.(row.symbol); },
    }, [icon(bookmarked ? 'bookmarkOn' : 'bookmark', 11)]),
    el('div', { class: 'coin-main' }, [
      el('div', { class: 'coin-top' }, [
        iconMoneda(row.symbol, 17),
        el('span', { class: 'coin-name', text: baseTicker(row.symbol) }),
        exchange ? el('span', { class: 'coin-ex', title: exchange }, [iconBursa(exchange, 11)]) : null,
        el('span', { class: `coin-change ${change >= 0 ? 'up' : 'down'}`, text: fmtPct(change) }),
      ]),
      el('div', { class: 'coin-bottom' }, [volume]),
    ]),
    el('span', { class: 'coin-spark' }, [sparkline(spark)]),
  ]);
}

/**
 * BTCUSDT -> BTC. USDT e cotarea implicită, deci o ascundem; orice altă cotare rămâne
 * vizibilă, altfel BTCUSDT și BTCUSDC ar arăta identic în listă.
 */
export function baseTicker(symbol) {
  if (symbol.endsWith('USDT')) return symbol.slice(0, -4) || symbol;
  const { base, quote } = splitSymbol(symbol);
  return quote ? `${base}·${quote}` : symbol;
}

/**
 * Dropdown folosit pentru bursă, sortare și filtrele de calendar.
 *
 * Meniul se montează pe `<body>` cu poziționare fixă, nu în interiorul butonului:
 * barele de sus au `overflow-x: auto` pentru derulare orizontală, iar un meniu
 * absolut în interiorul lor era tăiat și nu se vedea deloc.
 */
export function dropdown({ label, items, value, onSelect, className = '', prefix = '', lead = '', title = '' }) {
  const current = el('span', { class: 'dd-current' });
  const menu = el('div', { class: 'dd-menu' });
  const root = el('div', { class: `dd ${className}` });
  let open = false;

  const setValue = (v) => {
    const item = items.find((i) => i.value === v) || items[0];
    clear(current);
    // `nod` e o **fabrică**, nu un element: acelaşi nod nu poate sta şi pe buton, şi în
    // meniu — al doilea loc l-ar muta din primul.
    if (item?.nod) current.append(item.nod());
    else if (item?.icon) current.append(icon(item.icon, 13));
    current.append(el('span', { text: item?.label ?? label }));
  };

  const close = () => {
    if (!open) return;
    open = false;
    menu.remove();
    root.classList.remove('open');
    document.removeEventListener('mousedown', onOutside, true);
    window.removeEventListener('resize', close);
    window.removeEventListener('scroll', close, true);
  };

  const onOutside = (event) => {
    if (!menu.contains(event.target) && !root.contains(event.target)) close();
  };

  const show = () => {
    open = true;
    root.classList.add('open');
    document.body.append(menu);
    const rect = button.getBoundingClientRect();
    menu.style.minWidth = `${Math.max(rect.width, 150)}px`;
    menu.style.left = `${rect.left}px`;
    menu.style.top = `${rect.bottom + 4}px`;
    // Dacă nu încape sub buton, îl deschidem în sus.
    const menuRect = menu.getBoundingClientRect();
    if (menuRect.bottom > window.innerHeight - 8) {
      menu.style.top = `${Math.max(8, rect.top - menuRect.height - 4)}px`;
    }
    if (menuRect.right > window.innerWidth - 8) {
      menu.style.left = `${Math.max(8, window.innerWidth - menuRect.width - 8)}px`;
    }
    document.addEventListener('mousedown', onOutside, true);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
  };

  // `lead` — iconița fixă a câmpului, `prefix` — numele lui scris mic înaintea valorii.
  const button = el('button', {
    class: 'dd-button', title,
    onclick: (event) => { event.stopPropagation(); open ? close() : show(); },
  }, [
    lead ? el('span', { class: 'dd-lead' }, [icon(lead, 12)]) : null,
    prefix ? el('span', { class: 'dd-prefix', text: prefix }) : null,
    current, icon('chevronDown', 10),
  ].filter(Boolean));

  for (const item of items) {
    menu.append(el('button', {
      class: `dd-item${item.value === value ? ' active' : ''}`,
      onclick: () => {
        setValue(item.value);
        [...menu.children].forEach((c) => c.classList.remove('active'));
        menu.querySelector(`[data-value="${item.value}"]`)?.classList.add('active');
        close();
        onSelect(item.value);
      },
      dataset: { value: item.value },
    }, [
      item.nod ? item.nod() : (item.icon ? icon(item.icon, 13) : null),
      el('span', { text: item.label }),
    ].filter(Boolean)));
  }

  setValue(value);
  root.append(button);
  /** Schimbarea valorii din cod (de exemplu, un preset mută sortarea), fără `onSelect`. */
  root.setValue = (v) => {
    setValue(v);
    for (const c of menu.children) c.classList.toggle('active', c.dataset.value === String(v));
  };
  // Meniul stă pe <body>: dacă butonul iese din DOM cu meniul deschis, îl închidem,
  // altfel ar rămâne un panou orfan atârnat pe ecran.
  new MutationObserver((_, obs) => {
    if (!root.isConnected) { close(); obs.disconnect(); }
  }).observe(document.body, { childList: true, subtree: true });
  return root;
}

/** Comutator Фьючерсы / Спот. */
export function marketToggle(value, onChange, { withAll = false } = {}) {
  const group = el('div', { class: 'market-toggle' });
  const options = [
    { id: 'futures', label: 'Фьючерсы' },
    { id: 'spot', label: 'Спот' },
    ...(withAll ? [{ id: 'all', label: 'Все' }] : []),
  ];
  for (const option of options) {
    group.append(el('button', {
      class: `market-btn${option.id === value ? ' active' : ''}`,
      text: option.label,
      onclick: () => {
        [...group.children].forEach((c) => c.classList.remove('active'));
        group.querySelector(`[data-id="${option.id}"]`)?.classList.add('active');
        onChange(option.id);
      },
      dataset: { id: option.id },
    }));
  }
  return group;
}

/** Comutator on/off în stil iOS. */
/**
 * Câmp de parolă cu ochi.
 *
 * Într-un formular unde parola nu se vede, o greşeală de tastare se descoperă abia la
 * trimitere — iar la „repetă parola" se descoperă de două ori. Ochiul e singurul mod
 * onest de a-l lăsa pe om să verifice ce a scris.
 */
export function passwordField(placeholder, autocomplete = 'current-password') {
  // `name` pe langa `autocomplete`: managerele de parole mai vechi si unele tastaturi de
  // telefon se uita la nume inainte sa se uite la orice altceva.
  const input = el('input', {
    class: 'auth-input', type: 'password', placeholder, autocomplete,
    name: autocomplete === 'new-password' ? 'new-password' : 'password',
  });
  const buton = el('button', { class: 'auth-eye', type: 'button', title: 'Показать пароль' }, [icon('eye', 13)]);

  buton.onclick = (event) => {
    // Butonul stă într-un `<label>`: fără asta, clicul s-ar duce şi la câmp.
    event.preventDefault();
    event.stopPropagation();
    const ascunsa = input.type === 'password';
    input.type = ascunsa ? 'text' : 'password';
    clear(buton).append(icon(ascunsa ? 'eyeOff' : 'eye', 13));
    buton.title = ascunsa ? 'Скрыть пароль' : 'Показать пароль';
    input.focus();
  };

  return { field: el('label', { class: 'auth-field' }, [icon('lock', 13), input, buton]), input };
}

export function toggle(checked, onChange, { title = '', subtitle = '' } = {}) {
  const knob = el('span', { class: 'switch-knob' });
  const track = el('button', {
    class: `switch${checked ? ' on' : ''}`,
    onclick: (event) => {
      const next = !event.currentTarget.classList.contains('on');
      event.currentTarget.classList.toggle('on', next);
      onChange(next);
    },
  }, [knob]);

  if (!title) return track;
  return el('div', { class: 'switch-row' }, [
    el('div', { class: 'switch-text' }, [
      el('div', { class: 'switch-title', text: title }),
      subtitle ? el('div', { class: 'switch-sub', text: subtitle }) : null,
    ]),
    track,
  ]);
}

/** Modal generic cu titlu, corp și butoane. */
export function modal({ title, body, actions = [], width = 640, onClose }) {
  const close = () => { overlay.remove(); onClose?.(); };
  const overlay = el('div', {
    class: 'modal-overlay',
    onclick: (event) => { if (event.target === overlay) close(); },
  }, [
    el('div', { class: 'modal', style: { maxWidth: `${width}px` } }, [
      el('div', { class: 'modal-head' }, [
        el('div', { class: 'modal-title', text: title }),
        el('button', { class: 'modal-close', onclick: close }, [icon('close', 16)]),
      ]),
      el('div', { class: 'modal-body' }, [body]),
      actions.length
        ? el('div', { class: 'modal-actions' }, actions.map((a) => el('button', {
          class: `btn${a.primary ? ' primary' : ''}`,
          text: a.label,
          onclick: () => a.onClick?.(close),
        })))
        : null,
    ]),
  ]);
  document.body.append(overlay);
  const escape = (event) => { if (event.key === 'Escape') { close(); window.removeEventListener('keydown', escape); } };
  window.addEventListener('keydown', escape);
  return { overlay, close };
}

/** Notificare temporară în colț. */
export function toast(message, kind = 'info') {
  let host = document.querySelector('.toast-host');
  if (!host) {
    host = el('div', { class: 'toast-host' });
    document.body.append(host);
  }
  const node = el('div', { class: `toast toast-${kind}`, text: message });
  host.append(node);
  setTimeout(() => { node.classList.add('out'); setTimeout(() => node.remove(), 300); }, 2600);
}

/**
 * Meniul mic care se deschide sub un buton.
 *
 * Se inchide la primul clic in afara si la `Escape`. Nu-l punem in interiorul cardului,
 * ci pe `body`: un meniu prins intr-un card cu `overflow: hidden` s-ar taia exact cand
 * are mai multe randuri.
 */
export function contextMenu(anchor, items) {
  document.querySelector('.ctx-menu')?.remove();
  const menu = el('div', { class: 'ctx-menu' }, items.map((it) => el('button', {
    class: `ctx-item${it.danger ? ' danger' : ''}`,
    onclick: () => { menu.remove(); it.onClick?.(); },
  }, [it.icon ? icon(it.icon, 12) : null, el('span', { text: it.label })])));
  document.body.append(menu);

  // Ancorat sub buton, dar fara sa iasa din ecran pe dreapta sau pe jos.
  const r = anchor.getBoundingClientRect();
  menu.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - menu.offsetWidth - 12))}px`;
  menu.style.top = r.bottom + menu.offsetHeight + 12 > window.innerHeight
    ? `${Math.max(8, r.top - menu.offsetHeight - 6)}px`
    : `${r.bottom + 6}px`;

  const inchide = (e) => {
    if (e.type === 'keydown' && e.key !== 'Escape') return;
    if (e.type === 'mousedown' && menu.contains(e.target)) return;
    menu.remove();
    document.removeEventListener('mousedown', inchide);
    window.removeEventListener('keydown', inchide);
  };
  setTimeout(() => {
    document.addEventListener('mousedown', inchide);
    window.addEventListener('keydown', inchide);
  }, 0);
  return menu;
}
